import { ACTIVE_PIPELINE_STATUSES } from "@entities/brief/lib/constants";
import { useGenerateBriefAIOverview } from "@entities/brief/lib/hooks/ai/useGenerateBriefAIOverview";
import { useGetBriefAIOverview } from "@entities/brief/lib/hooks/ai/useGetBriefAIOverview";
import { useUpdateBriefAiSettings } from "@entities/brief/lib/hooks/ai/useUpdateBriefAiSettings";
import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import {
  BriefAiOverviewStatus,
  BriefNotificationActionType,
} from "@shared/lib/types/brief";
import { Button, Input, Popconfirm } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Flex } from "antd";
import { Pencil, Wand2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

interface AiFitCheckPromptPanelProps {
  isCreatorFitCheckEnabled: boolean;
}

function hasSavedOverview(aiOverview?: string) {
  return !!aiOverview?.trim();
}

export function AiFitCheckPromptPanel({
  isCreatorFitCheckEnabled,
}: AiFitCheckPromptPanelProps) {
  const { brief, briefId } = useBriefStore();
  const hasSavedAiOverview = hasSavedOverview(brief?.aiOverview);
  const [masterPrompt, setMasterPrompt] = useState(brief?.aiOverview ?? "");
  const [isEditing, setIsEditing] = useState(!hasSavedAiOverview);
  const [overviewId, setOverviewId] = useState<string | null>(null);
  const lastHandledGenerationRef = useRef<string | null>(null);

  const {
    generateBriefAiOverview,
    isGenerated,
    isGenerateError,
    data: generateResult,
  } = useGenerateBriefAIOverview();
  const { briefAiOverview } = useGetBriefAIOverview(briefId, overviewId ?? "");
  const { updateBriefAiSettings, isUpdating, isUpdated } =
    useUpdateBriefAiSettings();
  const { isBulkActions, bulkActions } = useBulkActions();

  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const pendingReanalyzeRef = useRef(false);
  const handledSaveSuccessRef = useRef(false);

  const isOverviewIncomplete = !brief?.formV2;
  const isGenerating = isGeneratingPrompt;

  useEffect(() => {
    if (!isEditing) {
      setMasterPrompt(brief?.aiOverview ?? "");
    }
  }, [brief?.aiOverview, isEditing]);

  useEffect(() => {
    setMasterPrompt(brief?.aiOverview ?? "");
    setIsEditing(!hasSavedOverview(brief?.aiOverview));
    handledSaveSuccessRef.current = false;
    pendingReanalyzeRef.current = false;
  }, [briefId]);

  useEffect(() => {
    if (!isUpdated || handledSaveSuccessRef.current) return;

    handledSaveSuccessRef.current = true;
    setIsEditing(false);

    if (pendingReanalyzeRef.current && briefId) {
      pendingReanalyzeRef.current = false;
      bulkActions({
        briefId,
        actionType: BriefNotificationActionType.reAnalyzeCreator,
        ids: [],
        notIncludeIds: [],
        isSelectAll: true,
        filterParams: { briefId, statuses: ACTIVE_PIPELINE_STATUSES },
        actionParams: {},
      });
    }
  }, [isUpdated, briefId, bulkActions]);

  useEffect(() => {
    const nextOverviewId = generateResult?.data?._id;
    if (
      !isGenerated ||
      !nextOverviewId ||
      lastHandledGenerationRef.current === nextOverviewId
    ) {
      return;
    }

    lastHandledGenerationRef.current = nextOverviewId;
    setOverviewId(nextOverviewId);
  }, [generateResult, isGenerated]);

  useEffect(() => {
    if (!isGenerated || generateResult?.data?._id) return;
    setIsGeneratingPrompt(false);
  }, [generateResult, isGenerated]);

  useEffect(() => {
    if (!isGenerateError) return;
    setIsGeneratingPrompt(false);
    setOverviewId(null);
  }, [isGenerateError]);

  useEffect(() => {
    if (!overviewId || !briefAiOverview) return;

    if (briefAiOverview.status === BriefAiOverviewStatus.Completed) {
      if (briefAiOverview.parsedResponse) {
        setMasterPrompt(briefAiOverview.parsedResponse);
        setIsEditing(true);
      }
      setOverviewId(null);
      setIsGeneratingPrompt(false);
      return;
    }

    if (briefAiOverview.status === BriefAiOverviewStatus.Failed) {
      ToastService.error("Failed to generate prompt from Overview.");
      setOverviewId(null);
      setIsGeneratingPrompt(false);
    }
  }, [briefAiOverview, overviewId]);

  const handleGenerate = () => {
    if (!briefId || !brief?.brandId || isOverviewIncomplete) return;
    lastHandledGenerationRef.current = null;
    setIsGeneratingPrompt(true);
    generateBriefAiOverview({ briefId, brandId: brief.brandId });
  };

  const saveSettings = () => {
    const trimmed = masterPrompt.trim();
    if (!trimmed || !briefId || !brief?.brandId) return;

    updateBriefAiSettings({
      briefId,
      brandId: brief.brandId,
      isCreatorFitCheckEnabled: true,
      aiOverview: trimmed,
    });
  };

  const handleSaveWithReanalyze = () => {
    pendingReanalyzeRef.current = true;
    handledSaveSuccessRef.current = false;
    saveSettings();
  };

  const handleSaveWithoutReanalyze = () => {
    pendingReanalyzeRef.current = false;
    handledSaveSuccessRef.current = false;
    saveSettings();
  };

  const handleEdit = () => {
    handledSaveSuccessRef.current = true;
    setIsEditing(true);
  };

  const isTextareaReadOnly = isGenerating || (hasSavedAiOverview && !isEditing);
  const saveDisabled =
    !isEditing || !masterPrompt.trim() || isGenerating || isUpdating;
  const showEditButton = hasSavedAiOverview && !isEditing;
  const saveLabel = isCreatorFitCheckEnabled ? "Save changes" : "Save & enable";

  return (
    <div className={styles.aiFitCheckPromptPanel}>
      <div className={styles.promptHeader}>
        <div className={styles.promptHeaderText}>
          <div className={styles.promptLabel}>Master prompt</div>
          <p className={styles.promptHint}>
            Pull in campaign context from Overview, or type your own
            instructions.
          </p>
        </div>
        <div className={styles.promptActions}>
          <DisabledPopover
            isDisabled={isOverviewIncomplete}
            content={
              isOverviewIncomplete
                ? "Please complete the brief overview to enable AI Fit Check."
                : ""
            }
          >
            <button
              type="button"
              className={styles.generateButton}
              onClick={handleGenerate}
              disabled={isGenerating || isOverviewIncomplete}
            >
              <Wand2 size={13} />
              Generate prompt from Overview
            </button>
          </DisabledPopover>
          <span className={styles.charCount}>
            {masterPrompt.trim().length.toLocaleString()} characters
          </span>
        </div>
      </div>

      <div className={styles.textareaWrap}>
        {isGenerating ? (
          <div className={styles.generatingOverlay}>
            <span className={styles.generatingText}>
              Generating prompt from Overview…
            </span>
          </div>
        ) : null}
        {isTextareaReadOnly ? (
          <div className={styles.textareaReadView}>
            {masterPrompt.trim() ? (
              masterPrompt
            ) : (
              <span className={styles.textareaReadViewPlaceholder}>
                Add your Fit Check prompt here.
              </span>
            )}
          </div>
        ) : (
          <Input.TextArea
            value={masterPrompt}
            onChange={(e) => setMasterPrompt(e.target.value)}
            className={styles.textarea}
            placeholder="Add your Fit Check prompt here."
            autoSize={{ minRows: 4 }}
          />
        )}
      </div>

      <Flex className={styles.footer}>
        {showEditButton ? (
          <Button
            type="default"
            className={styles.editButton}
            icon={<Pencil size={15} />}
            onClick={handleEdit}
            disabled={isGenerating}
            aria-label="Edit"
          />
        ) : null}
        <Popconfirm
          title="Reanalyze creators?"
          description="Do you want to reanalyze all previously analyzed creators with this updated prompt?"
          okText="Yes"
          cancelText="No"
          onConfirm={handleSaveWithReanalyze}
          onCancel={handleSaveWithoutReanalyze}
          disabled={saveDisabled}
        >
          <Button
            type="primary"
            className={styles.saveButton}
            disabled={saveDisabled}
            loading={isUpdating || isBulkActions}
          >
            {saveLabel}
          </Button>
        </Popconfirm>
      </Flex>
    </div>
  );
}
